import { SetStateAction, Suspense, useState } from "react";
import { Output } from "valibot";

import LoadingPlaceholder from "components/LoadingPlaceholder";
import MessageWithLinks from "components/MessageWithLinks";
import { BotInfoSchema, ChatDataSchema } from "components/SonicBot/schemas";
import { saveLikeDislike } from "shared/api";
import { useConfigContext } from "shared/hooks";
import { AppConfiguration, Literals, Source } from "shared/types";
import classNames from "shared/utils/classNames";

import JumpingDots from "./JumpingDots";
import LikeButton from "./LikeButton";
import MessageAvatar from "./MessageAvatar";
import Sources from "./Sources";

import "./index.css";

type Props = {
  id: string;
  botInfo: Output<typeof BotInfoSchema>;
  isSent: boolean;
  message: string;
  sources?: Source[];
  imageUrls?: string[];
  config: AppConfiguration;
  chatId: string;
  chatData: Output<typeof ChatDataSchema>;
  setChatData: React.Dispatch<SetStateAction<any[]>>;
  isLastMessage?: boolean;
  copyTexts: Literals;
  showLikeDislikeBtn?: boolean;
};

function MessageBubble(props: Props) {
  const { isIframe } = useConfigContext();

  const {
    id,
    botInfo,
    isSent,
    message,
    sources,
    config,
    chatId,
    chatData,
    setChatData,
    isLastMessage,
    copyTexts,
    showLikeDislikeBtn = true,
    imageUrls,
  } = props;

  const [likeStatus, setLikeStatus] = useState<string>("");

  const handleLikeDislike = (status: string) => {
    setLikeStatus(status);
    setChatData(
      chatData.map(item => {
        if (item.id === id) {
          return { ...item, like_status: status };
        }
        return item;
      }),
    );

    saveLikeDislike({
      token: config.token,
      chatId,
      messageId: id,
      likeStatus: status,
    });
  };

  const generationInProgress = !message.trim() && isSent === false;
  const showJumpingDots =
    generationInProgress || id === "jumping-dots-container";

  return (
    <div id={id} className={classNames("likes-container", isSent && "sent")}>
      <div className="hidden">
        <Suspense fallback="">
          <MessageWithLinks message="" isSent={isSent} />
        </Suspense>
      </div>
      <div
        className={classNames(
          "flex max-w-full gap-2",
          isIframe && "items-end rounded-lg p-4",
        )}
      >
        <MessageAvatar
          isSent={isSent}
          isIframe={isIframe}
          botAvatar={botInfo.bot_info_config.chatbot_avatar}
          isLastMessage={isLastMessage}
        />
        <div className={classNames("w-fit min-w-0", isSent && "ml-[60px]")}>
          <div>
            {imageUrls?.map((url, index) => {
              return (
                <img
                  key={index}
                  src={url}
                  alt="message"
                  width={250}
                  height={250}
                />
              );
            })}

            {showJumpingDots ? (
              <JumpingDots />
            ) : (
              <Suspense
                fallback={
                  <LoadingPlaceholder
                    generationLanguage={
                      botInfo.bot_prompt_config.generation_language
                    }
                  />
                }
              >
                <MessageWithLinks message={message} isSent={isSent} />
              </Suspense>
            )}

            {botInfo.bot_info_config.is_show_sources && !isSent && (
              <Sources sources={sources} copyTexts={copyTexts} />
            )}
          </div>
        </div>
      </div>
      {!isSent && id !== "-1" && showLikeDislikeBtn && (
        <div
          style={{
            display: "flex",
            justifyContent: "right",
            alignItems: "end",
            width: "100%",
            padding: "4px",
            margin: "4px",
            gap: "8px",
          }}
        >
          <LikeButton
            handleLikeDislike={handleLikeDislike}
            likeStatusType="liked"
            likeStatus={likeStatus}
          />
          <LikeButton
            handleLikeDislike={handleLikeDislike}
            likeStatusType="disliked"
            likeStatus={likeStatus}
          />
        </div>
      )}
    </div>
  );
}

export default MessageBubble;
